import { format } from 'date-fns';
import { cloneDeep } from 'lodash';
import { nanoid } from 'nanoid';

import {
  AVAILABLE_PANELISTS_CAP,
  DEFAULT_RESPONSE_COUNT,
  QUESTION_TYPES,
  QUESTION_TYPES_WITH_SUBQUESTIONS,
  SURVEY_TYPES,
  VIDEO_SINGLE_GROUP_RESPONSE_COUNT,
} from '@api/instant_survey';
import { addonTypes } from '@api/surveys';
import { isEUDomain } from '@helpers';
import { formatData, isBetweenMinAndMax } from '@utilities';

import { SURVEY_TEMPORARY_TITLE } from './constants';

const GRID_ROWS_PER_QUESTION = 4;
const ADVANCED_METHODOLOGY_PRICE = {
  completes: 300,
  credits: 1,
};
const isSameAddonTitle = (addon, title) => addon.title?.toLowerCase() === title.toLowerCase();
const ADVANCED_METHODOLOGIES = [
  {
    addonType: addonTypes.MAX_DIFF,
    key: 'maxDiff',
    label: 'Max Diff',
    matchesAddon: ({ addonType }) => addonType === addonTypes.MAX_DIFF,
    matchesQuestion: ({ type }) => type === QUESTION_TYPES.MAX_DIFF.value,
  },
  {
    addonType: addonTypes.TURF,
    key: 'turf',
    label: 'TURF',
    matchesAddon: ({ addonType }) => addonType === addonTypes.TURF,
    matchesQuestion: ({ turf, type }) => turf === true || type === QUESTION_TYPES.TURF.value,
  },
  {
    key: 'vanWestendorp',
    label: 'Van Westendorp',
    matchesAddon: (addon) => isSameAddonTitle(addon, 'Van Westendorp'),
    matchesQuestion: ({ type }) => type === QUESTION_TYPES.VAN_WESTENDORP.value,
  },
];

/**
 * format available panelists
 * @param  {Object} options.group
 * @param  {number} options.group.quotaLimit
 * @param  {number} options.group.validationUsers
 * @return {string}
 */
export const formatAvailablePanelists = ({ quotaLimit, validationUsers }) => {
  const cappedAvailablePanelists = Math.min(AVAILABLE_PANELISTS_CAP, 15 * quotaLimit);
  const displayedPanelistCount = Math.min(validationUsers, cappedAvailablePanelists);

  return `${formatData({ format: '0,0', value: displayedPanelistCount })}${
    validationUsers >= cappedAvailablePanelists ? '+' : ''
  }`;
};

/**
 * generate temporary survey title
 * @param  {Date} date
 * @return {string}
 */
export const generateTemporarySurveyTitle = (date = new Date()) =>
  `${SURVEY_TEMPORARY_TITLE} - ${format(date, 'MMM dd, yyyy')}`;

/**
 * @param {Object} [question]
 * @return {Boolean}
 */
export const isLanguageScreenerQuestion = (question) =>
  question?.properties?.isLanguageScreener === true;

export const SEQUENTIAL_MONADIC_CONCEPT_VALIDATION_MESSAGES = {
  CONCEPT_NAMES_REQUIRED: 'All concepts in a sequential monadic section must have names.',
  MINIMUM_CONCEPTS_REQUIRED: 'Sequential monadic sections require at least 2 concepts.',
  MINIMUM_SUBQUESTIONS_REQUIRED: 'Sequential monadic sections require at least 1 question.',
};

export const SEQUENTIAL_MONADIC_QUESTIONNAIRE_VALIDATION_MESSAGES = {
  CONCEPT_IMAGES_MATCH_REQUIRED:
    'All concepts in a sequential monadic section must either have images or have no images.',
  CONCEPT_MINIMUM_REQUIRED: 'Each sequential monadic section must have at least 2 concepts.',
  QUESTION_REQUIRED: 'Each sequential monadic section must have at least one question.',
};

const getActiveSequentialMonadicConcepts = (concepts = []) =>
  concepts.filter(({ _delete }) => !_delete);

const getAnswerableSequentialMonadicSubQuestions = (subQuestions = []) =>
  subQuestions.filter(
    ({ _delete, question_type, type }) =>
      !_delete && (type || question_type) !== QUESTION_TYPES.INSTRUCTIONS.value
  );

/**
 * get grid question cost - a grid is charged one question per block of rows, because the time and
 * effort required to answer 4 rows is similar to answering a single longer question
 * @param {Object[]} [subQuestions] grid rows
 * @returns {number}
 */
const getGridQuestionCost = (subQuestions = []) =>
  Math.max(
    1,
    Math.ceil(
      subQuestions.filter(({ isCarryForwardGenerated }) => !isCarryForwardGenerated).length /
        GRID_ROWS_PER_QUESTION
    )
  );

/**
 * get the cost of a single sequential monadic sub-question, before it is multiplied by the number of
 * active concepts. A nested grid is charged per block of rows exactly like a standalone grid, and
 * instructional text stays free.
 *
 * Sub-questions can reach this from paths that have not been camelCased yet (see
 * transformQuestionnaireJobResponse), so both type/question_type and subQuestions/sub_questions are
 * accepted.
 * @param {Object} [subQuestion]
 * @returns {number}
 */
export const getSequentialMonadicSubQuestionCost = ({
  _delete,
  question_type: questionType,
  sub_questions: rawSubQuestions,
  subQuestions,
  type,
} = {}) => {
  if (_delete) return 0;

  const resolvedType = type || questionType;

  if (resolvedType === QUESTION_TYPES.INSTRUCTIONS.value) return 0;
  if (resolvedType === QUESTION_TYPES['TABLE-RADIO'].value) {
    return getGridQuestionCost(subQuestions || rawSubQuestions);
  }

  return 1;
};

/**
 * get sequential monadic question cost - every sub-question is charged its own cost (a nested grid
 * costs one question per block of rows), and the total is multiplied by the number of active
 * concepts because panelists answer every sub-question once per concept
 * @param {Object} question
 * @param {Object[]} [question.concepts]
 * @param {Object[]} [question.subQuestions]
 * @returns {number}
 */
export const getSequentialMonadicQuestionCost = ({ concepts = [], subQuestions = [] } = {}) =>
  subQuestions.reduce(
    (cost, subQuestion) => cost + getSequentialMonadicSubQuestionCost(subQuestion),
    0
  ) * getActiveSequentialMonadicConcepts(concepts).length;

/**
 * check whether a question carries an image anywhere. Images can hang off the question prompt, any
 * answer option, or any nested sub-question (grid rows are transformed with transformQuestion, so
 * they carry the same shape and are walked recursively).
 * @param {Object} [question]
 * @param {Object[]} [question.answers]
 * @param {Object} [question.image]
 * @param {Object[]} [question.subQuestions]
 * @returns {boolean}
 */
export const hasQuestionImage = ({ answers, image, subQuestions } = {}) =>
  Boolean(image) ||
  (answers || []).some(({ image: answerImage }) => Boolean(answerImage)) ||
  (subQuestions || []).some(hasQuestionImage);

/**
 * validate sequential monadic question
 * @param {Object} question
 * @param {Object[]} [question.concepts]
 * @param {Object[]} [question.subQuestions]
 * @param {string} question.type
 * @returns {?string}
 */
export const validateSequentialMonadicQuestion = ({
  concepts = [],
  subQuestions = [],
  type,
} = {}) => {
  if (type !== QUESTION_TYPES.SEQUENTIAL_MONADIC.value) return null;

  const activeConcepts = getActiveSequentialMonadicConcepts(concepts);
  const answerableSubQuestions = getAnswerableSequentialMonadicSubQuestions(subQuestions);
  const allConceptsHaveNames = activeConcepts.every(({ name }) => Boolean(name?.trim()));

  if (activeConcepts.length < 2) {
    return SEQUENTIAL_MONADIC_CONCEPT_VALIDATION_MESSAGES.MINIMUM_CONCEPTS_REQUIRED;
  }

  if (answerableSubQuestions.length < 1) {
    return SEQUENTIAL_MONADIC_CONCEPT_VALIDATION_MESSAGES.MINIMUM_SUBQUESTIONS_REQUIRED;
  }

  if (!allConceptsHaveNames) {
    return SEQUENTIAL_MONADIC_CONCEPT_VALIDATION_MESSAGES.CONCEPT_NAMES_REQUIRED;
  }

  return null;
};

/**
 * Makes sure a monadic section has at least 2 concepts, at least 1 question,
 * all concepts have names, and all concepts either have images or don't have images.
 *
 * @param {Object} question
 * @param {Object[]} [question.concepts]
 * @param {Object[]} [question.subQuestions]
 * @param {string} question.type
 * @returns {?string}
 */
export const getSequentialMonadicQuestionnaireValidationError = ({
  concepts = [],
  subQuestions = [],
  type,
} = {}) => {
  if (type !== QUESTION_TYPES.SEQUENTIAL_MONADIC.value) return null;

  const activeConcepts = getActiveSequentialMonadicConcepts(concepts);
  const answerableSubQuestions = getAnswerableSequentialMonadicSubQuestions(subQuestions);

  if (activeConcepts.length < 2) {
    return SEQUENTIAL_MONADIC_QUESTIONNAIRE_VALIDATION_MESSAGES.CONCEPT_MINIMUM_REQUIRED;
  }

  if (answerableSubQuestions.length === 0) {
    return SEQUENTIAL_MONADIC_QUESTIONNAIRE_VALIDATION_MESSAGES.QUESTION_REQUIRED;
  }

  const allConceptsHaveNames = activeConcepts.every(({ name }) => Boolean(name?.trim()));
  if (!allConceptsHaveNames) {
    return SEQUENTIAL_MONADIC_CONCEPT_VALIDATION_MESSAGES.CONCEPT_NAMES_REQUIRED;
  }

  const conceptImageCount = activeConcepts.filter(({ image }) => Boolean(image)).length;
  if (conceptImageCount > 0 && conceptImageCount < activeConcepts.length) {
    return SEQUENTIAL_MONADIC_QUESTIONNAIRE_VALIDATION_MESSAGES.CONCEPT_IMAGES_MATCH_REQUIRED;
  }

  return null;
};

/**
 * get question cost
 * @param {Object} question
 * @param {boolean} question._delete
 * @param {Object[]} question.concepts
 * @param {Object} question.properties
 * @param {string} question.type
 * @returns {number} integer cost to compare against questionnaire pricing
 */
export const getQuestionCost = ({
  _delete,
  concepts,
  isScreener,
  properties,
  subQuestions,
  type,
}) => {
  if (_delete) return 0;
  if (isScreener) return 0;

  switch (type) {
    case QUESTION_TYPES.INSTRUCTIONS.value:
      return 0;
    case QUESTION_TYPES.MAX_DIFF.value:
      return Math.ceil(properties?.numberOfSets / 5);
    case QUESTION_TYPES.SEQUENTIAL_MONADIC.value:
      return getSequentialMonadicQuestionCost({ concepts, subQuestions });
    case QUESTION_TYPES['TABLE-RADIO'].value:
      return getGridQuestionCost(subQuestions);
    default:
      return 1;
  }
};

/**
 * get questions cost - reducer shortcut for get question cost
 * @param {Object[]} questions
 * @returns {number}
 */
export const getQuestionsCost = (questions = []) =>
  questions.reduce((count, question) => count + getQuestionCost(question), 0);

export const getQuestionsWithScreenersFirst = (questions = []) => {
  const activeScreeners = questions.filter(({ _delete, isScreener }) => !_delete && isScreener);
  const activeStandardQuestions = questions.filter(
    ({ _delete, isScreener }) => !_delete && !isScreener
  );
  const removedQuestions = questions.filter(({ _delete }) => _delete);

  return [...activeScreeners, ...activeStandardQuestions, ...removedQuestions];
};

export const isSequentialMonadicQuestion = (question = {}) =>
  question.type === QUESTION_TYPES.SEQUENTIAL_MONADIC.value;

export const isAttentionCheckQuestion = (question = {}) =>
  Array.isArray(question.answers) &&
  question.answers.some(({ _delete, isCorrectAnswer }) => !_delete && isCorrectAnswer);

const isSequentialMonadicSubQuestion = (question = {}) =>
  Boolean(question.sequentialMonadicQuestionId);

const isInstructionQuestion = (question = {}) =>
  (question.type || question.question_type) === QUESTION_TYPES.INSTRUCTIONS.value;

/**
 * Flattens top-level questions with sequential monadic sub-questions for logic lookup.
 * Sequential monadic containers stay in the list as targets, but are never valid sources.
 * @param {Object[]} questions
 * @returns {Object[]}
 */
export const getQuestionLogicQuestions = (questions = []) =>
  questions
    .filter(({ _delete }) => !_delete)
    .flatMap((question, questionIndex) => {
      if (!isSequentialMonadicQuestion(question)) {
        return [{ ...question, questionIndex }];
      }

      const subQuestions = (question.subQuestions || [])
        .filter(({ _delete }) => !_delete)
        .map((subQuestion, subQuestionIndex) => ({
          ...subQuestion,
          questionIndex,
          sequentialMonadicQuestionId: question.id,
          sequentialMonadicSubQuestionIndex: subQuestionIndex,
        }));

      return [
        {
          ...question,
          isSequentialMonadicContainer: true,
          questionIndex,
        },
        ...subQuestions,
      ];
    });

export const findQuestionLogicQuestionById = (questions = [], questionId) =>
  getQuestionLogicQuestions(questions).find(({ id }) => String(id) === String(questionId));

const isGlobalQuestionLogicSource = (question = {}) =>
  !question.isSequentialMonadicContainer &&
  !isSequentialMonadicSubQuestion(question) &&
  !isInstructionQuestion(question);

/**
 * Gets questions that can be used as answer-based logic sources for a target question.
 * @param {Object} options.currentQuestion
 * @param {Object[]} options.questions top-level survey questions
 * @returns {Object[]}
 */
export const getQuestionLogicSourceQuestions = ({ currentQuestion, questions = [] } = {}) => {
  const questionLogicQuestions = getQuestionLogicQuestions(questions);
  const currentQuestionMeta = questionLogicQuestions.find(
    ({ id }) => String(id) === String(currentQuestion?.id)
  );

  if (!currentQuestionMeta) return [];

  return questionLogicQuestions.filter((question) => {
    if (String(question.id) === String(currentQuestionMeta.id)) return false;

    if (isSequentialMonadicSubQuestion(currentQuestionMeta)) {
      if (isSequentialMonadicSubQuestion(question)) {
        return (
          !isInstructionQuestion(question) &&
          question.sequentialMonadicQuestionId ===
            currentQuestionMeta.sequentialMonadicQuestionId &&
          question.sequentialMonadicSubQuestionIndex <
            currentQuestionMeta.sequentialMonadicSubQuestionIndex
        );
      }

      return (
        isGlobalQuestionLogicSource(question) &&
        question.questionIndex < currentQuestionMeta.questionIndex
      );
    }

    return (
      isGlobalQuestionLogicSource(question) &&
      question.questionIndex < currentQuestionMeta.questionIndex
    );
  });
};

export const isValidQuestionLogicSource = ({ currentQuestion, questions, sourceQuestion }) =>
  getQuestionLogicSourceQuestions({ currentQuestion, questions }).some(
    ({ id }) => String(id) === String(sourceQuestion?.id)
  );

const getQuestionLogicSourceRows = (logic = {}) => [
  ...(logic.skip?.logic || []),
  ...Object.values(logic.option || {}).flatMap((optionLogic) => optionLogic?.logic || []),
];

/**
 * Detects invalid source references for logic involving sequential monadic
 * sub-questions after a proposed question order change.
 * @param {Object} options.questionLogic
 * @param {Object[]} options.questions
 * @returns {boolean}
 */
export const hasInvalidSequentialMonadicQuestionLogicSources = ({
  questionLogic = {},
  questions = [],
} = {}) => {
  const questionLogicQuestions = getQuestionLogicQuestions(questions);
  const findLogicQuestionById = (questionId) =>
    questionLogicQuestions.find(({ id }) => String(id) === String(questionId));

  return Object.entries(questionLogic || {}).some(([logicQuestionId, logic]) => {
    const currentQuestion = findLogicQuestionById(logicQuestionId);
    if (!currentQuestion) return false;

    const isCurrentSequentialMonadicSubQuestion = isSequentialMonadicSubQuestion(currentQuestion);

    return getQuestionLogicSourceRows(logic).some((row) => {
      if (!row?.question?.id) return false;

      const sourceQuestion = findLogicQuestionById(row.question.id) || row.question;
      const isSourceSequentialMonadicSubQuestion = isSequentialMonadicSubQuestion(sourceQuestion);

      if (!isCurrentSequentialMonadicSubQuestion && !isSourceSequentialMonadicSubQuestion) {
        return false;
      }

      return !isValidQuestionLogicSource({
        currentQuestion,
        questions,
        sourceQuestion: row.question,
      });
    });
  });
};

/**
 * getGroupIdsWithIntraGroupLogic
 * @param {Record<string, import('@api/instant_survey/types').QuestionLogic>} options.questionLogic
 * @param {import('@api/instant_survey/types').Question[]} options.questions
 * @returns {Array<string>} affected group ids (as strings)
 */
export const getGroupIdsWithIntraGroupLogic = ({ questionLogic = {}, questions = [] } = {}) => {
  const groupIdByQuestionId = new Map();
  questions.forEach((question) => {
    if (question.questionGroup != null) {
      groupIdByQuestionId.set(String(question.id), String(question.questionGroup));
    }
  });

  const affectedGroupIds = new Set();

  questions.forEach((question) => {
    const groupId = question.questionGroup;
    if (groupId == null) return;
    if (affectedGroupIds.has(String(groupId))) return;

    const logic = questionLogic[question.id] || {};
    const skipRows = logic.skip?.logic || [];
    const answerOptionRows = Object.values(logic.option || {}).flatMap(
      (optionLogic) => optionLogic?.logic || []
    );

    const dependsOnSameGroupMember = [...skipRows, ...answerOptionRows].some(
      (row) => groupIdByQuestionId.get(String(row?.question?.id)) === String(groupId)
    );

    if (dependsOnSameGroupMember) {
      affectedGroupIds.add(String(groupId));
    }
  });

  return [...affectedGroupIds];
};

/**
 * getRandomizedGroupsWithIntraGroupLogic
 * @param {Record<string, import('@api/instant_survey/types').QuestionGroup>} options.questionGroups
 * @param {Record<string, import('@api/instant_survey/types').QuestionLogic>} options.questionLogic
 * @param {import('@api/instant_survey/types').Question[]} options.questions
 * @returns {Array<string>} group ids (as strings) whose randomization should be removed
 */
export const getRandomizedGroupsWithIntraGroupLogic = ({
  questionGroups = {},
  questionLogic = {},
  questions = [],
} = {}) =>
  getGroupIdsWithIntraGroupLogic({ questionLogic, questions }).filter(
    (groupId) => questionGroups[String(groupId)]?.randomizeOrder
  );

/**
 * Count non-instruction questions before a given index
 * Used to determine if a question is beyond Q2 for disqualify logic rules
 * @param {Array} questions - Array of question objects
 * @param {number} index - Index of the current question
 * @returns {number} Count of non-instruction questions before this index
 */
export const getNonInstructionQuestionCountBefore = (questions, index) => {
  return questions.slice(0, index).filter(({ type }) => type !== QUESTION_TYPES.INSTRUCTIONS.value)
    .length;
};

/**
 * Calculate sequential starting question numbers for each question, used for numeric comparisons
 * (e.g. reorder logic, skip logic references). Returns raw integers, not display ranges.
 * Does not filter deleted questions, relies on getQuestionCost returning 0 for deleted entries.
 * For UI/DOCX display labels (e.g. "2-4"), use getQuestionDisplayIndexById.
 * @param  {Array} questionsList
 * @return {Object} mapping of question IDs to their starting question number
 */
export const calculateQuestionNumbers = (questionsList) => {
  return questionsList.reduce(
    (acc, question) => {
      const questionCost = getQuestionCost(question);
      acc.questionNumbersByQuestions[question.id] = acc.questionNumber;
      acc.questionNumber += questionCost;
      return acc;
    },
    {
      questionNumber: 1,
      questionNumbersByQuestions: {},
    }
  ).questionNumbersByQuestions;
};

/**
 * format the display index for a question occupying a number of consecutive slots
 * @param {number} startIndex
 * @param {number} cost
 * @returns {string} e.g. '3' or '3-5'
 */
const formatQuestionDisplayIndex = (startIndex, cost) =>
  `${startIndex}${cost > 1 ? `-${startIndex + cost - 1}` : ''}`;

/**
 * Map each active sequential monadic sub-question to the survey slot(s) it uses for one concept.
 * The parent section repeats those slots for every concept, while each sub-question label shows
 * only its own position within a single concept.
 * @param {Object} question
 * @param {number} startIndex
 * @returns {Object} mapping of sub-question IDs to display indexes
 */
const getSequentialMonadicSubQuestionDisplayIndexes = (question, startIndex) => {
  let currentIndex = startIndex;

  return (question.subQuestions || [])
    .filter(({ _delete }) => !_delete)
    .reduce((displayIndexes, subQuestion) => {
      if (isInstructionQuestion(subQuestion)) {
        displayIndexes[subQuestion.id] = 'T';
        return displayIndexes;
      }

      const subQuestionCost = getSequentialMonadicSubQuestionCost(subQuestion);
      displayIndexes[subQuestion.id] = formatQuestionDisplayIndex(currentIndex, subQuestionCost);
      currentIndex += subQuestionCost;
      return displayIndexes;
    }, {});
};

/**
 * Get display index (number or range) for each question
 * @param {Array} questions
 * @returns {Object} mapping of question IDs to display index (e.g., '2-3')
 */
export const getQuestionDisplayIndexById = (questions) => {
  let currentIndex = 1;
  let screenerIndex = 1;
  return questions
    .filter(({ _delete }) => !_delete)
    .reduce((acc, question) => {
      if (question.type === QUESTION_TYPES.INSTRUCTIONS.value) {
        acc[question.id] = 'T';
        return acc;
      }
      if (question.isScreener) {
        acc[question.id] = `S${screenerIndex}`;
        screenerIndex += 1;
        return acc;
      }
      const questionCost = getQuestionCost(question);
      acc[question.id] = formatQuestionDisplayIndex(currentIndex, questionCost);
      if (isSequentialMonadicQuestion(question)) {
        Object.assign(acc, getSequentialMonadicSubQuestionDisplayIndexes(question, currentIndex));
      }
      currentIndex += questionCost;
      return acc;
    }, {});
};

/**
 * @typedef MaxDiffQuestionInfo
 * @property {Function} getText
 * @property {boolean} hasInsufficientCoverage
 * @property {number} numberOfSets
 * @property {number} optionsPerSet
 * @property {number} questionCost
 * @property {number} timesPerExperiment
 */

/**
 * get Max Diff question information including cost, display counts, and formatted text
 * @param {Question} question
 * @returns {MaxDiffQuestionInfo}
 */
export const getMaxDiffQuestionInfo = (question) => {
  const questionCost = getQuestionCost(question);
  const answersCount = question.answers?.length ?? 0;
  const numberOfSets = question.properties?.numberOfSets;
  const optionsPerSet = question.properties?.optionsPerSet;
  const timesPerExperiment = answersCount
    ? Math.ceil((numberOfSets * optionsPerSet) / answersCount)
    : 0;
  const hasInsufficientCoverage = answersCount > 0 && numberOfSets * optionsPerSet < answersCount;

  return {
    getText: () =>
      `This will count as ${questionCost} question${questionCost !== 1 ? 's' : ''} for your survey. ` +
      `It will be displayed as ${numberOfSets} separate questions; each question will show ` +
      `${optionsPerSet} options; each option will show ${timesPerExperiment} time(s) in the experiment.`,
    hasInsufficientCoverage,
    numberOfSets,
    optionsPerSet,
    questionCost,
    timesPerExperiment,
  };
};

/**
 * get numeric question type descriptive text based on properties
 * @param {Object} properties
 * @returns {string}
 */
export const getNumericTypeText = (properties) => {
  const min = properties?.minNumber ?? '';
  const max = properties?.maxNumber ?? '';

  if (properties?.forceCurrency) {
    const currencySymbol = isEUDomain() ? '€' : '$';
    return `currency write-in between ${currencySymbol}${min} and ${currencySymbol}${max}`;
  }
  if (properties?.forcePercent) {
    return `percentage write-in between ${min}% and ${max}%`;
  }
  return `numeric write-in between ${min} and ${max}`;
};

/**
 * determine if survey questions qualify for the TURF addon credit
 * @param {Object[]} questions
 * @returns {Boolean}
 */
export const hasTurfAddonQuestion = (questions = []) =>
  questions.some(({ turf, type }) => turf === true || type === QUESTION_TYPES.TURF.value);

/**
 * determine if survey questions qualify for the Van Westendorp addon credit
 * @param {Object[]} questions
 * @returns {boolean}
 */
export const hasVanWestendorpAddonQuestion = (questions = []) =>
  questions.some(({ type }) => type === QUESTION_TYPES.VAN_WESTENDORP.value);

/**
 * @typedef {{ completes: number, credits: number }} AdvancedMethodologyPrices
 * @typedef {{ addonType?: number, key: string, label: string, prices: AdvancedMethodologyPrices }} ActiveAdvancedMethodology
 */

/**
 * Get active advanced methodology charges that are not already persisted as add-ons.
 * @param {Object} [options]
 * @param {Object[]} [options.activeQuestions]
 * @param {Object[]} [options.addons]
 * @returns {ActiveAdvancedMethodology[]}
 */
export const getActiveAdvancedMethodologies = ({ activeQuestions = [], addons = [] } = {}) => {
  return ADVANCED_METHODOLOGIES.filter(
    ({ matchesAddon, matchesQuestion }) =>
      activeQuestions.some(matchesQuestion) && !addons.some(matchesAddon)
  ).map(({ addonType, key, label }) => ({
    addonType,
    key,
    label,
    prices: { ...ADVANCED_METHODOLOGY_PRICE },
  }));
};

/**
 * Normalize persisted advanced methodology add-ons for the current survey pricing.
 * @param {Object[]} [options.addons]
 * @param {Object[]} [options.activeQuestions]
 * @returns {Object[]}
 */
export const normalizeAdvancedMethodologyAddons = ({ activeQuestions = [], addons = [] } = {}) => {
  return addons.map((addon) => {
    const methodology = ADVANCED_METHODOLOGIES.find(({ matchesAddon }) => matchesAddon(addon));

    if (!methodology) return addon;
    if (activeQuestions.length > 0 && !activeQuestions.some(methodology.matchesQuestion)) {
      return addon;
    }

    return {
      ...addon,
      prices: { ...ADVANCED_METHODOLOGY_PRICE },
    };
  });
};

/**
 * get current questionnaire price
 * @param  {Object[]} options.questionnairePricing
 * @param  {Object} options.survey
 * @return {Object}
 */
export const getCurrentQuestionnairePrice = ({ questionnairePricing, survey }) => {
  const totalCount = getQuestionsCost(survey.questions);
  return questionnairePricing?.find((p) => p.questions === totalCount);
};

/**
 * get current sample price
 * @param  {Object[]} options.samplePricing
 * @param  {Object} options.survey
 * @return {Object}
 */
export const getCurrentSamplePrice = ({ samplePricing, survey }) => {
  const activeGroups = survey.groups.filter(({ _delete }) => !_delete);

  return samplePricing?.find(
    ({ quotaGroups, responses }) =>
      quotaGroups === activeGroups.length &&
      responses === activeGroups.reduce((acc, { quotaLimit }) => acc + quotaLimit, 0)
  );
};

/**
 * get quota limit
 * @param {string} surveyTypeId
 * @return {number}
 */
export const getQuotaLimit = (surveyTypeId) => {
  if (surveyTypeId === SURVEY_TYPES.VIDEO) return VIDEO_SINGLE_GROUP_RESPONSE_COUNT;
  if (isEUDomain()) return DEFAULT_RESPONSE_COUNT;
  const surveyTypesWithHigherDefault = [
    'standard-v3-instant-survey-type',
    SURVEY_TYPES.MONADIC,
    'concept-test-v3-instant-survey-type',
    SURVEY_TYPES.TURF,
    SURVEY_TYPES.SEQUENTIAL_MONADIC,
    SURVEY_TYPES.NEW_ITEM,
  ];
  return surveyTypesWithHigherDefault.includes(surveyTypeId) ? 300 : DEFAULT_RESPONSE_COUNT;
};

/** get modal config based on survey type
 * @param {string} surveyType
 * @return {Object|null}
 */
export const getModalConfig = ({ surveyType }) => {
  switch (surveyType) {
    case 'monadic':
      return {
        description: `The Monadic Concept Test is used to evaluate multiple concepts—such as product designs,
          claims, or ads—by showing each respondent only one concept. This setup enables clean comparisons
          across distinct groups without bias from seeing multiple options.`,
        link: 'https://help.insights.numerator.com/en/articles/12141196-in-platform-survey-types-about-the-monadic-concept-test-survey',
        steps: [
          'Add concept images or names to each quota group when selecting panelists.',
          'Tag relevant questions to dynamically display each group’s assigned concept.',
          'The final report will compare results by concept, based on quota group exposure.',
        ],
        title: 'About the Monadic Concept Test Survey',
      };

    case 'new-item':
      return {
        description: `The New Item Survey helps you capture shopper reactions to newly released products.
        Use it to measure awareness, early perceptions, and purchase intent shortly after launch.`,
        link: 'https://help.numerator.com/en/articles/13455974-in-platform-survey-types-about-the-new-item-survey',
        steps: [
          'Targets items tagged as “New Items” in our system.',
          'Leverage point forward dates to activate continuously fed sample for up to 90 days.',
          'Follow the Help Center guidance to request manual tagging of specific New Items.',
        ],
        title: 'About the New Item Survey',
      };

    case 'sequential-monadic':
      return {
        description:
          'The Sequential Monadic Concept Test is used to evaluate multiple concepts—such as product designs, claims, or ads—by showing each respondent all concepts one at a time, in a randomized order. This method helps reduce order bias for comparisons across concepts.',
        link: 'https://help.insights.numerator.com/en/articles/12508219-in-platform-survey-types-about-the-sequential-monadic-concept-test',
        steps: [
          'Create question blocks tied to each concept and mark them for randomization.',
          'Respondents will see each concept’s set of questions in a unique, randomized sequence.',
          'The final report will display results by concept, with indicators showing which question blocks were randomized.',
        ],
        title: 'About the Sequential Monadic Concept Test Survey',
      };

    case 'turf':
      return {
        description: `TURF (Total Unduplicated Reach and Frequency) helps identify the optimal combination
        of options—such as features, flavors, or messages—that reaches the largest number of unique respondents.`,
        link: 'https://help.insights.numerator.com/en/articles/12333486-in-platform-survey-types-about-turf',
        steps: [
          'Select your desired panelist criteria.',
          'Add at least one TURF question during questionnaire creation.',
          'The final report will include a TURF analysis showing which combinations maximize reach.',
        ],
        title: 'About the TURF Survey',
      };

    default:
      return null;
  }
};

/**
 * Determines if a survey group is continuously fed based on its date field configuration.
 * A group is considered continuously fed if it has a DATE field with a single value,
 *
 * @param {Object} group
 * @param {Object[]} group.promptAnswers
 * @param {string} group.promptAnswers[].fieldType
 * @param {Array} group.promptAnswers[].values
 * @returns {boolean}
 *
 **/
export const isContinuouslyFed = (group) => {
  const dateField = group.promptAnswers.find((answer) => answer.fieldType === 'DATE');
  if (!dateField) return false;
  return dateField.values.length === 1 && typeof dateField.values[0] === 'string';
};

/**
 * Checks if question logic is complete.
 * @param {Object} questionLogic - Question logic object.
 * @returns {boolean} - True if complete, false otherwise.
 */
const hasLogicProperty = (row, property) => Object.prototype.hasOwnProperty.call(row, property);

const hasLogicSelections = (selections) => Boolean(selections?.length);

const isQuotaGroupLogicRowComplete = (row) => hasLogicSelections(row.values);

const isDisqualifyLogicRowComplete = (row) => {
  if (!row) return false;

  if (hasLogicProperty(row, 'url_variable')) {
    return isQuotaGroupLogicRowComplete(row);
  }

  return hasLogicSelections(row.answers);
};

const isSkipLogicRowComplete = (row) => {
  if (!row) return false;

  if (hasLogicProperty(row, 'url_variable')) {
    return isQuotaGroupLogicRowComplete(row);
  }

  if (hasLogicProperty(row, 'comparison_value')) {
    return (
      Boolean(row.question?.id) && row.comparison_value !== undefined && row.comparison_value !== ''
    );
  }

  return Boolean(row.question?.id) && hasLogicSelections(row.answers);
};

export const isQuestionLogicComplete = (questionLogic) => {
  if (!questionLogic || Object.keys(questionLogic).length === 0) return true;

  for (const [, logic] of Object.entries(questionLogic)) {
    if (!logic) return false;

    const disqualifyLogic = logic.disqualify;
    const disqualifyLogicRows = disqualifyLogic?.logic;
    if (Array.isArray(disqualifyLogicRows)) {
      for (const row of disqualifyLogicRows) {
        if (!isDisqualifyLogicRowComplete(row)) return false;
      }
    } else if (disqualifyLogic && !hasLogicSelections(disqualifyLogic.answers)) {
      return false;
    }

    const skipLogicRows = logic.skip?.logic;
    if (Array.isArray(skipLogicRows)) {
      for (const row of skipLogicRows) {
        if (!isSkipLogicRowComplete(row)) return false;
      }
    }
  }

  return true;
};

/**
 * Checks whether a question already has a backend id.
 * @param {?Object} question
 * @return {boolean}
 */
const hasPersistedId = (question) => question && question.id !== null && question.id !== undefined;

// Nested sequential-monadic questions receive temporary string ids in the editor. The request
// formatter deliberately omits those ids, so they must not be treated as backend-persisted when
// reconciling a stale autosave response.
const hasBackendQuestionId = (question) =>
  question && typeof question.id !== 'string' && question.id !== null && question.id !== undefined;

/**
 * Builds a stable identity string for a question using persisted id, client id, or temp signature.
 * @param {?Object} question
 * @return {string}
 */
export const createQuestionKey = (question) => {
  if (!question) return '';
  if (hasPersistedId(question)) return `id:${question.id}`;
  if (question.questionClientId) return `client:${question.questionClientId}`;
  return `temp:${question.text ?? ''}:${question.type ?? ''}:${question._tmpKey ?? ''}`;
};

/**
 * Reports whether another question group already uses `name`. Names are compared trimmed and
 * case-insensitively because the group pickers ("Move to: <name>") only show the name, "Concept"
 * and "CONCEPT" are indistinguishable there.
 * @param {Object} options
 * @param {?number} [options.excludeGroupId] the group being renamed; its own name is not a conflict
 * @param {string} options.name
 * @param {Object} [options.questionGroups] keyed by group id
 * @return {boolean}
 */
export const isQuestionGroupNameTaken = ({ excludeGroupId = null, name, questionGroups = {} }) => {
  const normalizedName = (name || '').trim().toLowerCase();

  return Object.values(questionGroups).some(
    (group) =>
      group.id !== excludeGroupId && (group.name || '').trim().toLowerCase() === normalizedName
  );
};

/**
 * Combines local and server question data, keeping client ids and `_delete` flag when needed.
 * @param {Object} prevQuestion
 * @param {Object} responseQuestion
 * @return {Object}
 */
const mergeQuestionRecord = (prevQuestion, responseQuestion) => {
  const mergedQuestion = {
    ...responseQuestion,
    ...(Array.isArray(responseQuestion.concepts) && {
      concepts: ensureConceptClientIds(responseQuestion.concepts, prevQuestion.concepts),
    }),
    questionClientId:
      prevQuestion.questionClientId ?? responseQuestion.questionClientId ?? nanoid(),
  };

  if (responseQuestion._delete === undefined && prevQuestion._delete !== undefined) {
    mergedQuestion._delete = prevQuestion._delete;
  }

  return mergedQuestion;
};

const ensureConceptClientIds = (concepts = [], prevConcepts = []) => {
  if (!Array.isArray(concepts) || concepts.length === 0) return concepts;

  const clientIdByPersistedId = new Map(
    (prevConcepts || [])
      .filter((concept) => concept?.conceptClientId && hasPersistedId(concept))
      .map((concept) => [String(concept.id), concept.conceptClientId])
  );

  return concepts.map((concept) => {
    if (!concept) return concept;
    if (concept.conceptClientId) return concept;

    const persistedId = hasPersistedId(concept) ? String(concept.id) : null;
    return {
      ...concept,
      conceptClientId: (persistedId && clientIdByPersistedId.get(persistedId)) || nanoid(),
    };
  });
};

/**
 * Ensures every question has a `questionClientId`, reusing previous ids when possible.
 * @param {Object[]} [questions=[]]
 * @param {Object[]} [prevQuestions=[]]
 * @return {Object[]}
 */
export const ensureQuestionClientIds = (questions = [], prevQuestions = []) => {
  if (!Array.isArray(questions) || questions.length === 0) {
    return questions;
  }

  const clientIdByPersistedId = new Map(
    prevQuestions
      .filter((question) => question?.questionClientId && hasPersistedId(question))
      .map((question) => [String(question.id), question.questionClientId])
  );
  const prevQuestionByClientId = new Map(
    prevQuestions
      .filter((question) => question?.questionClientId)
      .map((question) => [question.questionClientId, question])
  );
  const prevQuestionByPersistedId = new Map(
    prevQuestions.filter(hasPersistedId).map((question) => [String(question.id), question])
  );

  return questions.map((question) => {
    if (!question) return question;

    const persistedId = hasPersistedId(question) ? String(question.id) : null;
    const questionClientId =
      question.questionClientId ||
      (persistedId && clientIdByPersistedId.get(persistedId)) ||
      nanoid();
    const prevQuestion =
      (persistedId && prevQuestionByPersistedId.get(persistedId)) ||
      prevQuestionByClientId.get(questionClientId);

    return {
      ...question,
      ...(Array.isArray(question.concepts) && {
        concepts: ensureConceptClientIds(question.concepts, prevQuestion?.concepts),
      }),
      questionClientId,
    };
  });
};

/**
 * Drops translation entries flagged `_delete` once a successful save has honored them, so the
 * deleted rows aren't re-sent on every subsequent save. Handles question-, option- (answers), and
 * concept- (groups) level translations; other fields (incl. row-level `_delete`) are untouched.
 * @param {Object[]} [entities=[]] questions or groups
 * @return {Object[]}
 */
export const sweepDeletedTranslations = (entities = []) =>
  (entities || []).map((entity) => {
    if (!entity) return entity;
    const next = { ...entity };
    if (Array.isArray(next.translations)) {
      next.translations = next.translations.filter((translation) => !translation?._delete);
    }
    if (Array.isArray(next.answers)) {
      next.answers = next.answers.map((answer) =>
        answer && Array.isArray(answer.translations)
          ? { ...answer, translations: answer.translations.filter((t) => !t?._delete) }
          : answer
      );
    }
    return next;
  });

/**
 * @param {?Object} entity question or answer
 * @return {String}
 */
const translationSourceText = (entity) => entity?.richText || entity?.text || '';
const TRANSLATABLE_QUESTION_PROPERTY_TRANSLATIONS = [
  { sourceKey: 'bestLabel', translationsKey: 'bestLabelTranslations' },
  { sourceKey: 'worstLabel', translationsKey: 'worstLabelTranslations' },
];

/**
 * Returns a copy of a translations array with every entry flagged `isRelevant: false`, leaving
 * `isUserUpdated` (and the translated text itself) untouched.
 * @param {Object[]} [translations]
 * @return {Object[]}
 */
const markTranslationsStale = (translations) =>
  (translations || []).map((translation) =>
    translation ? { ...translation, isRelevant: false } : translation
  );

/**
 * Carries existing translations onto an edited question and marks translations stale when
 * their source text changed.
 */
export const reconcileTranslationRelevance = (previousQuestion, nextQuestion) => {
  if (!previousQuestion || !nextQuestion) return nextQuestion;

  const reconciled = { ...nextQuestion };

  const previousQuestionTranslations = previousQuestion.translations;
  if (Array.isArray(previousQuestionTranslations) && previousQuestionTranslations.length) {
    const questionSourceChanged =
      translationSourceText(previousQuestion) !== translationSourceText(nextQuestion);
    reconciled.translations = questionSourceChanged
      ? markTranslationsStale(previousQuestionTranslations)
      : previousQuestionTranslations;
  }

  if (Array.isArray(nextQuestion.answers)) {
    const previousAnswersById = new Map(
      (previousQuestion.answers || []).map((answer) => [String(answer.id), answer])
    );
    reconciled.answers = nextQuestion.answers.map((answer) => {
      const previousAnswer = previousAnswersById.get(String(answer.id));
      const previousAnswerTranslations = previousAnswer?.translations;
      if (!Array.isArray(previousAnswerTranslations) || !previousAnswerTranslations.length) {
        return answer;
      }
      const answerSourceChanged =
        translationSourceText(previousAnswer) !== translationSourceText(answer);
      return {
        ...answer,
        translations: answerSourceChanged
          ? markTranslationsStale(previousAnswerTranslations)
          : previousAnswerTranslations,
      };
    });
  }

  TRANSLATABLE_QUESTION_PROPERTY_TRANSLATIONS.forEach(({ sourceKey, translationsKey }) => {
    const previousTranslations = previousQuestion.properties?.[translationsKey];
    if (!Array.isArray(previousTranslations) || !previousTranslations.length) return;

    const propertySourceChanged =
      (previousQuestion.properties?.[sourceKey] || '') !==
      (nextQuestion.properties?.[sourceKey] || '');

    reconciled.properties = {
      ...(reconciled.properties || {}),
      [translationsKey]: propertySourceChanged
        ? markTranslationsStale(previousTranslations)
        : previousTranslations,
    };
  });

  if (
    QUESTION_TYPES_WITH_SUBQUESTIONS.includes(nextQuestion.type) &&
    Array.isArray(nextQuestion.subQuestions)
  ) {
    const previousSubQuestionsById = new Map(
      (previousQuestion.subQuestions || []).map((subQuestion) => [
        String(subQuestion.id),
        subQuestion,
      ])
    );
    reconciled.subQuestions = nextQuestion.subQuestions.map((subQuestion) => {
      const previousSubQuestion = previousSubQuestionsById.get(String(subQuestion.id));
      const previousSubQuestionTranslations = previousSubQuestion?.translations;
      if (
        !Array.isArray(previousSubQuestionTranslations) ||
        !previousSubQuestionTranslations.length
      ) {
        return subQuestion;
      }
      const subQuestionSourceChanged =
        translationSourceText(previousSubQuestion) !== translationSourceText(subQuestion);
      return {
        ...subQuestion,
        translations: subQuestionSourceChanged
          ? markTranslationsStale(previousSubQuestionTranslations)
          : previousSubQuestionTranslations,
      };
    });
  }

  return reconciled;
};

/**
 * Whether an edit to a question changed any translatable source text: the question's own text or
 * the set of its (active) option and grid-row texts, an edited, added, or removed item. Uses the
 * same source-text rule as {@link reconcileTranslationRelevance} (rich text preferred), so the
 * "translations are stale" marking and the "review translations again" gate agree.
 * @param {?import('@api/instant_survey/utilities/types').Question} previousQuestion pre-edit question
 * @param {?import('@api/instant_survey/utilities/types').Question} nextQuestion edited question
 * @return {boolean}
 */
export const didQuestionSourceTextChange = (previousQuestion, nextQuestion) => {
  if (!previousQuestion || !nextQuestion) return false;
  if (translationSourceText(previousQuestion) !== translationSourceText(nextQuestion)) return true;

  const didQuestionPropertySourceChange = TRANSLATABLE_QUESTION_PROPERTY_TRANSLATIONS.some(
    ({ sourceKey }) =>
      (previousQuestion.properties?.[sourceKey] || '') !==
      (nextQuestion.properties?.[sourceKey] || '')
  );
  if (didQuestionPropertySourceChange) return true;

  const activeAnswers = (question) => (question.answers || []).filter((answer) => !answer._delete);
  const previousAnswers = activeAnswers(previousQuestion);
  const nextAnswers = activeAnswers(nextQuestion);
  // An option added or removed changes what needs translating.
  if (previousAnswers.length !== nextAnswers.length) return true;

  const previousAnswersById = new Map(previousAnswers.map((answer) => [String(answer.id), answer]));
  const didAnswerSourceChange = nextAnswers.some((answer) => {
    const previousAnswer = previousAnswersById.get(String(answer.id));
    // A brand-new option (unmatched id) or one whose text changed.
    return (
      !previousAnswer || translationSourceText(previousAnswer) !== translationSourceText(answer)
    );
  });
  if (didAnswerSourceChange) return true;

  const hasTranslatableSubQuestions = [previousQuestion.type, nextQuestion.type].some((type) =>
    QUESTION_TYPES_WITH_SUBQUESTIONS.includes(type)
  );
  if (!hasTranslatableSubQuestions) return false;

  const activeSubQuestions = (question) =>
    (question.subQuestions || []).filter((subQuestion) => !subQuestion._delete);
  const previousSubQuestions = activeSubQuestions(previousQuestion);
  const nextSubQuestions = activeSubQuestions(nextQuestion);
  // A grid row added or removed changes what needs translating.
  if (previousSubQuestions.length !== nextSubQuestions.length) return true;

  const previousSubQuestionsById = new Map(
    previousSubQuestions.map((subQuestion) => [String(subQuestion.id), subQuestion])
  );
  return nextSubQuestions.some((subQuestion) => {
    const previousSubQuestion = previousSubQuestionsById.get(String(subQuestion.id));
    // A brand-new grid row (unmatched id) or one whose text changed.
    return (
      !previousSubQuestion ||
      translationSourceText(previousSubQuestion) !== translationSourceText(subQuestion)
    );
  });
};

/**
 * Reconciles questionnaire arrays by overlaying response updates while keeping unsaved locals.
 * @param {Object} params
 * @param {Object[]} [params.prevQuestions=[]]
 * @param {Object[]|undefined} params.responseQuestions
 * @return {Object[]}
 */
export const mergeQuestions = ({ prevQuestions = [], responseQuestions }) => {
  const normalizedPrev = ensureQuestionClientIds(prevQuestions);

  if (responseQuestions === undefined) {
    return normalizedPrev;
  }

  if (!Array.isArray(responseQuestions)) {
    return normalizedPrev;
  }

  const normalizedResponse = ensureQuestionClientIds(responseQuestions, normalizedPrev);
  const responseByKey = new Map();

  normalizedResponse.forEach((question) => {
    responseByKey.set(createQuestionKey(question), question);
  });

  const merged = [];

  normalizedPrev.forEach((prevQuestion) => {
    if (!prevQuestion) return;
    const key = createQuestionKey(prevQuestion);

    if (responseByKey.has(key)) {
      const responseQuestion = responseByKey.get(key);
      responseByKey.delete(key);
      merged.push(mergeQuestionRecord(prevQuestion, responseQuestion));
      return;
    }

    merged.push(prevQuestion);
  });

  normalizedResponse.forEach((question) => {
    const key = createQuestionKey(question);
    if (!responseByKey.has(key)) return;
    responseByKey.delete(key);
    merged.push(question);
  });

  return merged;
};

/**
 * Merges questionLogic maps, keeping logic for still-present questions even if response omitted them.
 * @param {Object} params
 * @param {Object[]} [params.mergedQuestions=[]]
 * @param {Object} [params.prevLogic={}]
 * @param {Object|undefined} [params.responseLogic={}]
 * @return {Object}
 */
export const mergeQuestionLogic = ({
  mergedQuestions = [],
  prevLogic = {},
  responseLogic = {},
}) => {
  if (responseLogic === undefined) return prevLogic;

  const merged = { ...responseLogic };
  const validQuestionIds = new Set(
    mergedQuestions
      .map((question) => question?.id)
      .filter((id) => id !== undefined && id !== null)
      .map((id) => String(id))
  );

  Object.entries(prevLogic || {}).forEach(([questionId, logic]) => {
    if (!validQuestionIds.has(questionId)) return;
    if (!Object.prototype.hasOwnProperty.call(merged, questionId)) {
      merged[questionId] = logic;
    }
  });

  return merged;
};

/**
 * Builds a `questionClientId -> responseQuestion` bridge for unsaved questions using a
 * request-time snapshot. The backend doesn't echo `questionClientId`, so we use:
 *   Pass 1: positional alignment with a `text + type` guard at each slot (handles the common
 *   case where formatQuestion / transformQuestion preserve order end-to-end).
 *   Pass 2: `text + type` fallback for snapshot entries Pass 1 missed, gated by uniqueness on
 *   both sides. When two unsaved questions share `text + type` we cannot safely disambiguate
 *   them after a BE reorder, so we deliberately leave them unmatched rather than risk mis-graft.
 * @param {?Object} requestSnapshot
 * @param {Object[]} responseQuestions
 * @return {Map<string, Object>}
 */
const buildQuestionClientIdToResponse = (requestSnapshot, responseQuestions) => {
  const bridge = new Map();
  if (!requestSnapshot || !Array.isArray(requestSnapshot.questions)) return bridge;
  if (!Array.isArray(responseQuestions)) return bridge;

  const snapshotQuestions = requestSnapshot.questions;
  const consumedResponseIndices = new Set();

  // Response slots whose id was already persisted at request-send time are not eligible to be
  // grafted onto unsaved locals, because doing so would silently overwrite the existing record on the
  // next save. Excluded from both Pass 1 (positional) and Pass 2 (uniqueness) candidacy.
  const persistedSnapshotQuestionIds = new Set(
    snapshotQuestions
      .filter((snapshot) => snapshot?.id !== null && snapshot?.id !== undefined)
      .map((snapshot) => String(snapshot.id))
  );
  const isPersistedResponseId = (responseQuestion) =>
    persistedSnapshotQuestionIds.has(String(responseQuestion.id));

  const minLength = Math.min(snapshotQuestions.length, responseQuestions.length);
  for (let index = 0; index < minLength; index += 1) {
    const snapshot = snapshotQuestions[index];
    const responseQuestion = responseQuestions[index];
    if (!snapshot?.questionClientId) continue;
    if (snapshot.id !== null && snapshot.id !== undefined) continue;
    if (!responseQuestion) continue;
    if (responseQuestion.id === null || responseQuestion.id === undefined) continue;
    if (isPersistedResponseId(responseQuestion)) continue;
    if ((snapshot.text ?? '') !== (responseQuestion.text ?? '')) continue;
    if ((snapshot.type ?? '') !== (responseQuestion.type ?? '')) continue;
    bridge.set(snapshot.questionClientId, responseQuestion);
    consumedResponseIndices.add(index);
  }

  const buildTextTypeCounts = (items) => {
    const counts = new Map();
    items.forEach((item) => {
      if (!item) return;
      const key = `${item.text ?? ''}::${item.type ?? ''}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  };

  const unmatchedSnapshot = snapshotQuestions.filter(
    (snapshot) =>
      snapshot?.questionClientId &&
      (snapshot.id === null || snapshot.id === undefined) &&
      !bridge.has(snapshot.questionClientId)
  );
  const unmatchedResponse = responseQuestions
    .map((responseQuestion, index) => ({ index, responseQuestion }))
    .filter(
      ({ index, responseQuestion }) =>
        !consumedResponseIndices.has(index) &&
        responseQuestion &&
        responseQuestion.id !== null &&
        responseQuestion.id !== undefined &&
        !isPersistedResponseId(responseQuestion)
    );

  const snapshotCounts = buildTextTypeCounts(unmatchedSnapshot);
  const responseCounts = buildTextTypeCounts(
    unmatchedResponse.map(({ responseQuestion }) => responseQuestion)
  );
  const responseByUniqueKey = new Map();
  unmatchedResponse.forEach(({ responseQuestion }) => {
    const key = `${responseQuestion.text ?? ''}::${responseQuestion.type ?? ''}`;
    if (responseCounts.get(key) === 1) responseByUniqueKey.set(key, responseQuestion);
  });

  unmatchedSnapshot.forEach((snapshot) => {
    const key = `${snapshot.text ?? ''}::${snapshot.type ?? ''}`;
    if (snapshotCounts.get(key) !== 1) return;
    const responseQuestion = responseByUniqueKey.get(key);
    if (!responseQuestion) return;
    bridge.set(snapshot.questionClientId, responseQuestion);
  });

  return bridge;
};

const buildConceptClientIdToResponse = (snapshotQuestion, responseQuestion) => {
  const bridge = new Map();
  const snapshotConcepts = Array.isArray(snapshotQuestion?.concepts)
    ? snapshotQuestion.concepts.filter((concept) => !concept?._delete)
    : [];
  const responseConcepts = Array.isArray(responseQuestion?.concepts)
    ? responseQuestion.concepts.filter((concept) => !concept?._delete)
    : [];
  const persistedSnapshotIds = new Set(
    snapshotConcepts.filter(hasPersistedId).map((concept) => String(concept.id))
  );
  const consumedResponseIndices = new Set();
  const conceptKey = (concept) =>
    `${concept?.name ?? ''}::${concept?.imageId ?? concept?.image?.id ?? ''}`;

  const minLength = Math.min(snapshotConcepts.length, responseConcepts.length);
  for (let index = 0; index < minLength; index += 1) {
    const snapshotConcept = snapshotConcepts[index];
    const responseConcept = responseConcepts[index];
    if (!snapshotConcept?.conceptClientId || hasPersistedId(snapshotConcept)) continue;
    if (!hasPersistedId(responseConcept)) continue;
    if (persistedSnapshotIds.has(String(responseConcept.id))) continue;
    if (conceptKey(snapshotConcept) !== conceptKey(responseConcept)) continue;
    bridge.set(snapshotConcept.conceptClientId, responseConcept);
    consumedResponseIndices.add(index);
  }

  const unmatchedSnapshot = snapshotConcepts.filter(
    (concept) =>
      concept?.conceptClientId && !hasPersistedId(concept) && !bridge.has(concept.conceptClientId)
  );
  const unmatchedResponse = responseConcepts.filter(
    (concept, index) =>
      !consumedResponseIndices.has(index) &&
      hasPersistedId(concept) &&
      !persistedSnapshotIds.has(String(concept.id))
  );
  const snapshotCounts = new Map();
  const responseCounts = new Map();
  unmatchedSnapshot.forEach((concept) => {
    const key = conceptKey(concept);
    snapshotCounts.set(key, (snapshotCounts.get(key) || 0) + 1);
  });
  unmatchedResponse.forEach((concept) => {
    const key = conceptKey(concept);
    responseCounts.set(key, (responseCounts.get(key) || 0) + 1);
  });
  const responseByUniqueKey = new Map(
    unmatchedResponse
      .filter((concept) => responseCounts.get(conceptKey(concept)) === 1)
      .map((concept) => [conceptKey(concept), concept])
  );
  unmatchedSnapshot.forEach((concept) => {
    const key = conceptKey(concept);
    if (snapshotCounts.get(key) !== 1) return;
    const responseConcept = responseByUniqueKey.get(key);
    if (responseConcept) bridge.set(concept.conceptClientId, responseConcept);
  });

  return bridge;
};

const mergeSequentialMonadicConceptIds = ({
  localQuestion,
  responseQuestion,
  snapshotQuestion,
}) => {
  if (
    localQuestion?.type !== QUESTION_TYPES.SEQUENTIAL_MONADIC.value ||
    !Array.isArray(localQuestion.concepts) ||
    !snapshotQuestion ||
    !responseQuestion
  ) {
    return localQuestion;
  }

  const conceptBridge = buildConceptClientIdToResponse(snapshotQuestion, responseQuestion);
  return {
    ...localQuestion,
    concepts: localQuestion.concepts.map((concept) => {
      if (!concept || hasPersistedId(concept) || !concept.conceptClientId) return concept;
      const responseConcept = conceptBridge.get(concept.conceptClientId);
      return responseConcept ? { ...concept, id: responseConcept.id } : concept;
    }),
  };
};

const getQuestionIdentityKey = (question = {}) => `${question.text ?? ''}::${question.type ?? ''}`;

const getSnapshotQuestionForLocal = ({ localQuestion, requestSnapshot }) => {
  const snapshotQuestions = requestSnapshot?.questions || [];

  if (hasBackendQuestionId(localQuestion)) {
    return snapshotQuestions.find((question) => String(question?.id) === String(localQuestion.id));
  }

  return snapshotQuestions.find(
    (question) => question?.questionClientId === localQuestion?.questionClientId
  );
};

/**
 * Grafts backend ids from a stale response onto locally edited sequential-monadic children.
 * Matching is based on the request snapshot, rather than current local child content, because
 * that content may have changed after the request was sent.
 */
const mergeSequentialMonadicSubQuestionIds = ({
  localQuestion,
  responseQuestion,
  snapshotQuestion,
}) => {
  if (localQuestion?.type !== QUESTION_TYPES.SEQUENTIAL_MONADIC.value) {
    return localQuestion;
  }

  const localSubQuestions = localQuestion?.subQuestions;
  const responseSubQuestions = responseQuestion?.subQuestions;
  const snapshotSubQuestions = snapshotQuestion?.subQuestions;

  if (
    !Array.isArray(localSubQuestions) ||
    !Array.isArray(responseSubQuestions) ||
    !Array.isArray(snapshotSubQuestions)
  ) {
    return localQuestion;
  }

  const persistedSnapshotIds = new Set(
    snapshotSubQuestions.filter(hasBackendQuestionId).map((subQuestion) => String(subQuestion.id))
  );
  const unsavedSnapshotSubQuestions = snapshotSubQuestions.filter(
    (subQuestion) => !hasBackendQuestionId(subQuestion)
  );
  const createdResponseSubQuestions = responseSubQuestions.filter(
    (subQuestion) =>
      hasBackendQuestionId(subQuestion) && !persistedSnapshotIds.has(String(subQuestion.id))
  );
  const snapshotKeyCounts = new Map();
  const responseKeyCounts = new Map();

  unsavedSnapshotSubQuestions.forEach((snapshotSubQuestion) => {
    const key = getQuestionIdentityKey(snapshotSubQuestion);
    snapshotKeyCounts.set(key, (snapshotKeyCounts.get(key) || 0) + 1);
  });
  createdResponseSubQuestions.forEach((responseSubQuestion) => {
    const key = getQuestionIdentityKey(responseSubQuestion);
    responseKeyCounts.set(key, (responseKeyCounts.get(key) || 0) + 1);
  });

  const responseBySnapshotIndex = new Map();
  snapshotSubQuestions.forEach((snapshotSubQuestion, index) => {
    if (hasBackendQuestionId(snapshotSubQuestion)) return;
    const key = getQuestionIdentityKey(snapshotSubQuestion);
    if (snapshotKeyCounts.get(key) !== 1 || responseKeyCounts.get(key) !== 1) return;
    const responseSubQuestion = createdResponseSubQuestions.find(
      (candidate) => getQuestionIdentityKey(candidate) === key
    );
    if (responseSubQuestion) responseBySnapshotIndex.set(index, responseSubQuestion);
  });

  const snapshotIndexByLocalId = new Map(
    snapshotSubQuestions
      .map((subQuestion, index) => ({ index, subQuestion }))
      .filter(({ subQuestion }) => subQuestion?.id !== null && subQuestion?.id !== undefined)
      .map(({ index, subQuestion }) => [String(subQuestion.id), index])
  );

  const localSubQuestionIds = new Set(
    localSubQuestions
      .filter((subQuestion) => subQuestion?.id !== null && subQuestion?.id !== undefined)
      .map((subQuestion) => String(subQuestion.id))
  );
  const deletedSubQuestions = snapshotSubQuestions.flatMap((snapshotSubQuestion, index) => {
    if (
      hasBackendQuestionId(snapshotSubQuestion) ||
      localSubQuestionIds.has(String(snapshotSubQuestion?.id))
    ) {
      return [];
    }

    const responseSubQuestion = responseBySnapshotIndex.get(index);
    return responseSubQuestion ? [{ ...responseSubQuestion, _delete: true }] : [];
  });

  const subQuestions = localSubQuestions.map((localSubQuestion, index) => {
    if (hasBackendQuestionId(localSubQuestion)) return localSubQuestion;

    const snapshotIndex =
      localSubQuestion?.id !== null && localSubQuestion?.id !== undefined
        ? snapshotIndexByLocalId.get(String(localSubQuestion.id))
        : index;
    const responseSubQuestion = responseBySnapshotIndex.get(snapshotIndex);
    return responseSubQuestion
      ? { ...localSubQuestion, id: responseSubQuestion.id }
      : localSubQuestion;
  });

  return { ...localQuestion, subQuestions: [...subQuestions, ...deletedSubQuestions] };
};

const normalizeGroupName = (name) => name?.trim().toLowerCase() ?? '';

/**
 * Produces a stable string key from a group's promptAnswers for use in bridge signature
 * matching. Keyed on audience-value ids (or JSON for id-less values) so the key is invariant
 * to whether the promptAnswer row itself has been persisted yet.
 */
export const buildPromptAnswersKey = (promptAnswers) => {
  if (!Array.isArray(promptAnswers)) return '';
  return promptAnswers
    .filter((a) => a && !a._delete)
    .map((a) => {
      const valueKey = Array.isArray(a.values)
        ? a.values
            .map((v) =>
              v !== null && typeof v === 'object' && v.id !== null && v.id !== undefined
                ? String(v.id)
                : JSON.stringify(v)
            )
            .sort()
            .join(',')
        : '';
      return `${a.promptId ?? ''}:${a.field ?? ''}:${a.operator ?? ''}:${valueKey}`;
    })
    .sort()
    .join('|');
};

/**
 * Snapshot groups carry a pre-computed `promptAnswersKey`; response groups carry the raw
 * `promptAnswers` array. Both cases are handled so the key is consistent across both sides
 * of the bridge match.
 */
const buildGroupSignatureKey = (group) => {
  const promptKey =
    group?.promptAnswersKey !== undefined
      ? group.promptAnswersKey
      : buildPromptAnswersKey(group?.promptAnswers);
  return [
    normalizeGroupName(group?.name),
    group?.quotaLimit ?? '',
    group?.genRepSampleType ?? '',
    promptKey,
  ].join('::');
};

/**
 * Builds a `clientId -> responseGroup` bridge for unsaved groups using a request-time snapshot.
 * Two-pass match strategy:
 *   Pass 1: composite signature `(name, quotaLimit, genRepSampleType)`. Only consumes when the
 *   signature is unique on both sides so groups that differ in any captured attribute are paired
 *   deterministically even when names collide.
 *   Pass 2: `name`-only fallback gated by uniqueness on both sides. When two unsaved groups share
 *   a name and aren't disambiguated by Pass 1, we deliberately leave them unmatched rather than
 *   risk grafting an id onto the wrong row.
 * @param {?Object} requestSnapshot
 * @param {Object[]} responseGroups
 * @return {Map<string, Object>}
 */
const buildClientIdToResponseGroup = (requestSnapshot, responseGroups) => {
  const bridge = new Map();
  if (!requestSnapshot || !Array.isArray(requestSnapshot.groups)) return bridge;
  if (!Array.isArray(responseGroups)) return bridge;

  const persistedSnapshotIds = new Set(
    requestSnapshot.groups
      .filter((group) => group?.id !== null && group?.id !== undefined)
      .map((group) => String(group.id))
  );

  const unsavedSnapshot = requestSnapshot.groups.filter(
    (group) => group?.clientId && (group.id === null || group.id === undefined)
  );
  const unmatchedResponse = responseGroups.filter(
    (responseGroup) =>
      responseGroup &&
      responseGroup.id !== null &&
      responseGroup.id !== undefined &&
      !persistedSnapshotIds.has(String(responseGroup.id))
  );

  const matchUniquePairs = (snapshotGroups, candidateGroups, keyFn) => {
    const snapshotCounts = new Map();
    snapshotGroups.forEach((group) => {
      const key = keyFn(group);
      snapshotCounts.set(key, (snapshotCounts.get(key) || 0) + 1);
    });
    const responseCounts = new Map();
    candidateGroups.forEach((group) => {
      const key = keyFn(group);
      responseCounts.set(key, (responseCounts.get(key) || 0) + 1);
    });
    const responseByKey = new Map();
    candidateGroups.forEach((group) => {
      const key = keyFn(group);
      if (responseCounts.get(key) === 1) responseByKey.set(key, group);
    });
    const consumed = new Set();
    snapshotGroups.forEach((snapshotGroup) => {
      if (bridge.has(snapshotGroup.clientId)) return;
      const key = keyFn(snapshotGroup);
      if (snapshotCounts.get(key) !== 1) return;
      const responseGroup = responseByKey.get(key);
      if (!responseGroup) return;
      bridge.set(snapshotGroup.clientId, responseGroup);
      consumed.add(responseGroup);
    });
    return consumed;
  };

  const consumedBySignature = matchUniquePairs(
    unsavedSnapshot,
    unmatchedResponse,
    buildGroupSignatureKey
  );
  const remainingResponse = unmatchedResponse.filter((group) => !consumedBySignature.has(group));
  matchUniquePairs(unsavedSnapshot, remainingResponse, (group) => normalizeGroupName(group?.name));

  return bridge;
};

/**
 * Picks the response group that should donate ids to a local group. Matches by persisted id first,
 * and falls back to a unique name match for unsaved locals (since a stale response cannot contain
 * the very latest local edits, a value-aware signature would miss those rows).
 * @param {Object} localGroup
 * @param {Object[]} responseGroups
 * @return {?Object}
 */
const pickResponseGroupForIdMerge = (localGroup, responseGroups) => {
  if (!localGroup || !Array.isArray(responseGroups)) return null;

  if (localGroup.id !== null && localGroup.id !== undefined) {
    return (
      responseGroups.find(
        (group) => group?.id !== null && group?.id !== undefined && group.id === localGroup.id
      ) || null
    );
  }

  const localName = localGroup.name?.trim().toLowerCase();
  if (!localName) return null;

  const matches = responseGroups.filter(
    (group) =>
      group?.id !== null &&
      group?.id !== undefined &&
      group.name?.trim().toLowerCase() === localName
  );

  return matches.length === 1 ? matches[0] : null;
};

const hasPersistedRowId = (row) => row && row.id !== null && row.id !== undefined;

/**
 * Drops promptAnswers locally marked `_delete: true` with a persisted id that the response no
 * longer includes. The backend has already honored those deletions, so re-shipping them on the
 * next save would 404 on `InstantSurveyGroupPromptAnswer NNN does not exist`. Rows without a
 * persisted id are kept (the formatter strips them on send anyway) and non-deleted rows pass
 * through unchanged to preserve order and identity.
 * @param {Object[]} localAnswers
 * @param {Object[]} responseAnswers
 * @return {Object[]}
 */
const sweepConfirmedAnswerDeletions = (localAnswers, responseAnswers) => {
  if (!Array.isArray(localAnswers)) return localAnswers;
  const responseIds = new Set(
    (Array.isArray(responseAnswers) ? responseAnswers : [])
      .filter(hasPersistedRowId)
      .map((answer) => String(answer.id))
  );
  return localAnswers.filter((answer) => {
    if (!answer?._delete) return true;
    if (!hasPersistedRowId(answer)) return true;
    return responseIds.has(String(answer.id));
  });
};

/**
 * Drops groups locally marked `_delete: true` with a persisted id that the response no longer
 * includes. Without this sweep, the next autosave would re-ship the delete for a record the
 * backend already removed, which can fail the save and block subsequent quota-group deletions.
 * @param {Object[]} localGroups
 * @param {Object[]} responseGroups
 * @return {Object[]}
 */
const sweepConfirmedGroupDeletions = (localGroups, responseGroups) => {
  if (!Array.isArray(localGroups)) return localGroups;
  const responseIds = new Set(
    (Array.isArray(responseGroups) ? responseGroups : [])
      .filter(hasPersistedRowId)
      .map((group) => String(group.id))
  );
  return localGroups.filter((group) => {
    if (!group?._delete) return true;
    if (!hasPersistedRowId(group)) return true;
    return responseIds.has(String(group.id));
  });
};

/**
 * Returns a clone of `localGroup` with any missing ids copied over from `responseGroup`. Local
 * `values`, `_delete` flags, and other in-progress edits are preserved so newer changes survive.
 * Response answer ids are consumed one-to-one to avoid assigning the same backend row to two
 * local rows in degenerate (multi-row, same prompt/field/operator) cases. Local promptAnswers
 * that were already marked `_delete: true` and have been honored by the backend (no longer in
 * `responseGroup.promptAnswers`) are dropped so the next save can't re-delete a missing record.
 * @param {Object} localGroup
 * @param {Object} responseGroup
 * @param {Object} [options]
 * @param {boolean} [options.includeValidationFields]
 * @return {Object}
 */
const mergeGroupIdsFromResponse = (localGroup, responseGroup, { includeValidationFields } = {}) => {
  const merged = { ...localGroup };
  if (!hasPersistedRowId(localGroup) && hasPersistedRowId(responseGroup)) {
    merged.id = responseGroup.id;
  }

  if (includeValidationFields) {
    ['validationDatetime', 'validationStatus', 'validationUniqueUsers', 'validationUsers'].forEach(
      (field) => {
        if (Object.prototype.hasOwnProperty.call(responseGroup, field)) {
          merged[field] = responseGroup[field];
        }
      }
    );
  }

  // Graft backend-assigned concept-name translation ids onto unsaved local translations,
  // matched by languageCode (the (group, language) unique key). Without this, the next save
  // re-sends the translation without an id and the backend rejects the duplicate insert.
  if (Array.isArray(localGroup.translations)) {
    const responseTranslationsByLanguage = new Map(
      (Array.isArray(responseGroup.translations) ? responseGroup.translations : [])
        .filter((translation) => translation && hasPersistedRowId(translation))
        .map((translation) => [translation.languageCode, translation])
    );
    merged.translations = localGroup.translations
      .filter((localTranslation) => {
        if (!localTranslation?._delete) return true;
        const responseTranslation = responseTranslationsByLanguage.get(
          localTranslation.languageCode
        );
        return Boolean(responseTranslation?._delete);
      })
      .map((localTranslation) => {
        if (!localTranslation || hasPersistedRowId(localTranslation)) return localTranslation;
        const responseTranslation = responseTranslationsByLanguage.get(
          localTranslation.languageCode
        );
        return responseTranslation
          ? { ...localTranslation, id: responseTranslation.id }
          : localTranslation;
      });
  }

  if (!Array.isArray(localGroup.promptAnswers)) {
    return merged;
  }

  const responseAnswers = Array.isArray(responseGroup.promptAnswers)
    ? responseGroup.promptAnswers
    : [];

  const sweptAnswers = sweepConfirmedAnswerDeletions(localGroup.promptAnswers, responseAnswers);

  // Pre-index response answers into FIFO buckets keyed by (promptId, field, operator) so the
  // merge is O(n + m). A head-index per bucket preserves the same first-match order the prior
  // `.find` produced while keeping consumption O(1), since Array.shift would degrade to O(k²) in
  // pathological cases where many response answers share a key.
  const responseAnswerBuckets = new Map();
  responseAnswers.forEach((responseAnswer) => {
    if (!responseAnswer) return;
    if (!hasPersistedRowId(responseAnswer)) return;
    if (responseAnswer._delete) return;
    const key = `${responseAnswer.promptId}::${responseAnswer.field}::${
      responseAnswer.operator ?? ''
    }`;
    if (!responseAnswerBuckets.has(key)) responseAnswerBuckets.set(key, { head: 0, items: [] });
    responseAnswerBuckets.get(key).items.push(responseAnswer);
  });

  merged.promptAnswers = sweptAnswers.map((localAnswer) => {
    if (!localAnswer) return localAnswer;
    if (hasPersistedRowId(localAnswer)) return localAnswer;

    const key = `${localAnswer.promptId}::${localAnswer.field}::${localAnswer.operator ?? ''}`;
    const bucket = responseAnswerBuckets.get(key);
    if (!bucket || bucket.head >= bucket.items.length) return localAnswer;
    const matchingResponseAnswer = bucket.items[bucket.head];
    bucket.head += 1;
    return { ...localAnswer, id: matchingResponseAnswer.id };
  });

  return merged;
};

/**
 * Reconciles local groups with an autosave response on the "fresh" path (no local edits during
 * the in-flight save). Uses local groups as the spine so order, clientIds, and any in-progress
 * UI state survive React reconciliation, then grafts backend ids onto unsaved rows and drops
 * local rows whose deletion the backend has already honored. Newer groups discovered in the
 * response that don't bridge to any local row are appended so the user doesn't lose visibility
 * of server-side additions.
 * @param {Object} params
 * @param {Object[]} [params.prevGroups=[]]
 * @param {?Object} [params.requestSnapshot] see {@link mergeServerIdsFromStaleResponse}
 * @param {Object[]|undefined} params.responseGroups
 * @return {Object[]}
 */
export const mergeGroups = ({ prevGroups = [], requestSnapshot, responseGroups }) => {
  if (!Array.isArray(prevGroups)) return prevGroups;
  if (!Array.isArray(responseGroups)) return prevGroups;

  const clientIdBridge = buildClientIdToResponseGroup(requestSnapshot, responseGroups);
  const sweptGroups = sweepConfirmedGroupDeletions(prevGroups, responseGroups);
  const matchedResponseIds = new Set();

  const merged = sweptGroups.map((localGroup) => {
    if (!localGroup) return localGroup;

    let responseGroup = null;
    if (hasPersistedRowId(localGroup)) {
      responseGroup = pickResponseGroupForIdMerge(localGroup, responseGroups);
    } else if (localGroup.clientId && clientIdBridge.has(localGroup.clientId)) {
      responseGroup = clientIdBridge.get(localGroup.clientId);
    }

    if (!responseGroup) return localGroup;
    matchedResponseIds.add(String(responseGroup.id));
    return mergeGroupIdsFromResponse(localGroup, responseGroup, { includeValidationFields: true });
  });

  responseGroups.forEach((responseGroup) => {
    if (!hasPersistedRowId(responseGroup)) return;
    if (matchedResponseIds.has(String(responseGroup.id))) return;
    merged.push({
      ...responseGroup,
      clientId: responseGroup.clientId ?? nanoid(),
    });
  });

  return merged;
};

/**
 * Reconciles backend-assigned ids from a stale autosave response into the current local survey
 * without clobbering newer in-flight edits. This avoids the "duplicate prompt / duplicate group"
 * problem caused by dropping the response wholesale and then re-sending unsaved rows that the
 * backend then persists a second time.
 * @param {Object} params
 * @param {Object} params.prevSurvey
 * @param {?Object} [params.requestSnapshot] snapshot of groups and questions captured at
 *   request-send time. `groups` entries carry (clientId, id, name, quotaLimit, genRepSampleType)
 *   and bridge unsaved local groups to response ids via composite signature with name-uniqueness
 *   fallback. `questions` entries carry (questionClientId, id, text, type) and bridge unsaved
 *   local questions to response ids via positional + uniqueness-gated text/type matching.
 *   Question snapshots also carry concept client ids and request-time signatures so newly saved
 *   concepts can receive response ids without overwriting newer local edits. Both sides exclude
 *   response ids that were already persisted at request-send time so the merge cannot silently
 *   overwrite existing records.
 * @param {Object|undefined} params.responseSurvey
 * @return {Object}
 */
export const mergeServerIdsFromStaleResponse = ({
  prevSurvey,
  requestSnapshot,
  responseSurvey,
}) => {
  if (!prevSurvey || !responseSurvey) return prevSurvey;

  const merged = { ...prevSurvey };

  if (
    (prevSurvey.id === null || prevSurvey.id === undefined) &&
    responseSurvey.id !== null &&
    responseSurvey.id !== undefined
  ) {
    merged.id = responseSurvey.id;
  }

  if (Array.isArray(prevSurvey.groups) && Array.isArray(responseSurvey.groups)) {
    const clientIdBridge = buildClientIdToResponseGroup(requestSnapshot, responseSurvey.groups);
    const sweptGroups = sweepConfirmedGroupDeletions(prevSurvey.groups, responseSurvey.groups);

    merged.groups = sweptGroups.map((localGroup) => {
      if (hasPersistedRowId(localGroup)) {
        const responseGroup = pickResponseGroupForIdMerge(localGroup, responseSurvey.groups);
        if (!responseGroup) return localGroup;
        return mergeGroupIdsFromResponse(localGroup, responseGroup);
      }

      if (localGroup?.clientId && clientIdBridge.has(localGroup.clientId)) {
        return mergeGroupIdsFromResponse(localGroup, clientIdBridge.get(localGroup.clientId));
      }

      // With a request snapshot we trust the bridge as the only safe match for unsaved locals;
      // skip the name fallback so a group inserted after request-send can't absorb a stale id.
      if (requestSnapshot) return localGroup;

      const responseGroup = pickResponseGroupForIdMerge(localGroup, responseSurvey.groups);
      if (!responseGroup) return localGroup;
      return mergeGroupIdsFromResponse(localGroup, responseGroup);
    });
  }

  if (Array.isArray(prevSurvey.questions) && Array.isArray(responseSurvey.questions)) {
    const questionBridge = buildQuestionClientIdToResponse(
      requestSnapshot,
      responseSurvey.questions
    );

    merged.questions = prevSurvey.questions.map((localQuestion) => {
      if (!localQuestion) return localQuestion;
      const snapshotQuestion = getSnapshotQuestionForLocal({ localQuestion, requestSnapshot });
      const responseQuestion = hasBackendQuestionId(localQuestion)
        ? responseSurvey.questions.find(
            (question) => String(question?.id) === String(localQuestion.id)
          )
        : questionBridge.get(localQuestion.questionClientId);

      const questionWithSubQuestionIds = mergeSequentialMonadicSubQuestionIds({
        localQuestion,
        responseQuestion,
        snapshotQuestion,
      });
      const questionWithChildIds = mergeSequentialMonadicConceptIds({
        localQuestion: questionWithSubQuestionIds,
        responseQuestion,
        snapshotQuestion,
      });

      if (hasPersistedId(localQuestion)) return questionWithChildIds;

      if (localQuestion.questionClientId && questionBridge.has(localQuestion.questionClientId)) {
        return { ...questionWithChildIds, id: responseQuestion.id };
      }

      return questionWithChildIds;
    });
  }

  return merged;
};

/**
 * Pure evaluator for question logic. Validates all skip/disqualify rows and returns an annotated
 * deep copy of questionLogic with error flags set on each row. Does not call updateSurvey.
 * @param {Object} options.activeQuestions - non-deleted survey questions
 * @param {Object[]} options.groups - survey quota groups
 * @param {Object} options.questionLogic
 * @returns {{ annotatedQuestionLogic: Object, isValid: boolean }}
 */
export const evaluateQuestionLogic = ({ activeQuestions, groups, questionLogic }) => {
  const annotatedQuestionLogic = cloneDeep(questionLogic);
  let isValid = true;

  const isInvalidQuestionSource = ({ logicQuestion, sourceQuestion }) =>
    !sourceQuestion?.id ||
    !logicQuestion ||
    !isValidQuestionLogicSource({
      currentQuestion: logicQuestion,
      questions: activeQuestions,
      sourceQuestion,
    });

  for (const questionId in annotatedQuestionLogic) {
    const question = annotatedQuestionLogic[questionId];
    const logicQuestion = findQuestionLogicQuestionById(activeQuestions, questionId);

    if (question.skip) {
      for (const row of question.skip.logic) {
        if ('url_variable' in row) {
          const quotaGroupIds = groups.map(({ id }) => id);
          if (!row.values || row.values.length === 0) {
            row.hasAnswerError = true;
            isValid = false;
          } else {
            let quotaGroupsValid = true;
            for (const groupID of row.values) {
              if (!quotaGroupIds.includes(Number(groupID))) {
                quotaGroupsValid = false;
              }
            }
            if (!quotaGroupsValid) {
              row.hasAnswerError = true;
              isValid = false;
            } else {
              row.hasAnswerError = false;
            }
          }
        } else if ('comparison_value' in row) {
          if (isInvalidQuestionSource({ logicQuestion, sourceQuestion: row.question })) {
            row.hasQuestionError = true;
            row.isFirstQuestionError = isValid;
            isValid = false;
          } else {
            row.hasQuestionError = false;
          }

          if (
            row.comparison_value === undefined ||
            row.comparison_value === null ||
            row.comparison_value === '' ||
            (row.question?.type === QUESTION_TYPES.NUMERIC.value &&
              !isBetweenMinAndMax({
                max: row.question.properties?.maxNumber,
                min: row.question.properties?.minNumber,
                value: row.comparison_value,
              }))
          ) {
            row.hasAnswerError = true;
            row.isFirstAnswerError = isValid;
            isValid = false;
          } else {
            row.hasAnswerError = false;
          }
        } else if ('answers' in row) {
          if (isInvalidQuestionSource({ logicQuestion, sourceQuestion: row.question })) {
            row.hasQuestionError = true;
            row.isFirstQuestionError = isValid;
            isValid = false;
          } else {
            row.hasQuestionError = false;
          }
          if ((row.answers || []).length === 0) {
            row.hasAnswerError = true;
            row.isFirstAnswerError = isValid;
            isValid = false;
          } else {
            row.hasAnswerError = false;
          }
        }
      }
    }

    if (question.disqualify) {
      const disqualifyLogic = question.disqualify;
      if ('logic' in disqualifyLogic && Array.isArray(disqualifyLogic.logic)) {
        for (const row of disqualifyLogic.logic) {
          if ('url_variable' in row) {
            if ((row.values || []).length === 0) {
              row.hasAnswerError = true;
              isValid = false;
            } else {
              row.hasAnswerError = false;
            }
          } else if ('answers' in row && (row.answers || []).length === 0) {
            row.isFirstAnswerError = isValid;
            row.hasAnswerError = true;
            isValid = false;
          } else {
            row.hasAnswerError = false;
          }
        }
      } else if ('answers' in disqualifyLogic && (disqualifyLogic.answers || []).length === 0) {
        disqualifyLogic.isFirstAnswerError = isValid;
        disqualifyLogic.hasAnswerError = true;
        isValid = false;
      } else if ('hasAnswerError' in disqualifyLogic) {
        disqualifyLogic.hasAnswerError = false;
      }
    }
  }

  return { annotatedQuestionLogic, isValid };
};
